import React, { useEffect, useState } from "react";
import {
  Loader,
  Copy,
  Check,
  Sparkles,
  RefreshCw,
  ExternalLink,
  MessageSquare,
  Search,
} from "lucide-react";

const API_BASE = "https://api.growl.games/api/geo-answers";

interface GeoAnswerRow {
  id: number;
  platform: string;
  url: string;
  question: string;
  keyword: string;
  account: string;
  answer: string;
  alt_answer: string;
  meta: string;
  posted: boolean;
  posted_at: string | null;
  created_at: string;
}

interface ProspectRow {
  id: number;
  platform: string;
  url: string;
  title: string;
  snippet: string;
  score: number;
  reason: string;
  keyword: string;
  brand_eligible: boolean;
  region_note: string;
  status: string;
  updated_at: string;
  answered_list?: string[];
  remaining_accounts?: string[];
  cooling_until?: string | null;
}

const ACCOUNT_LABEL: Record<string, string> = {
  bryce: "Bryce", growl: "Growl", rajveer: "Rajveer", staack: "Staack",
};

interface GenerateResult {
  id: number | null;
  platform: string;
  answer: string;
  alt_answer: string;
  meta: string;
  model: string;
}

type Phase = "new" | "warming" | "established";

const PHASES: Phase[] = ["new", "warming", "established"];

const authHeaders = () => {
  const token = localStorage.getItem("token") || "";
  return {
    "Content-Type": "application/json",
    Authorization: token ? `Bearer ${token}` : "",
  };
};

const CopyButton: React.FC<{ text: string; label?: string }> = ({ text, label }) => {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };
  return (
    <button
      onClick={copy}
      className={`flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
        copied ? "bg-green-600 text-white" : "bg-blue-600 hover:bg-blue-700 text-white"
      }`}
    >
      {copied ? <Check size={13} /> : <Copy size={13} />}
      {copied ? "Copied!" : label || "Copy"}
    </button>
  );
};

const GeoAnswers: React.FC = () => {
  const [url, setUrl] = useState("");
  const [content, setContent] = useState("");
  const [keyword, setKeyword] = useState("");
  const [account, setAccount] = useState<string>("auto");
  const [phaseBryce, setPhaseBryce] = useState<Phase>(
    (localStorage.getItem("geo_phase_bryce") as Phase) || "new"
  );
  const [phaseGrowl, setPhaseGrowl] = useState<Phase>(
    (localStorage.getItem("geo_phase_growl") as Phase) || "new"
  );
  const [phaseRajveer, setPhaseRajveer] = useState<Phase>(
    (localStorage.getItem("geo_phase_rajveer") as Phase) || "new"
  );
  const [phaseStaack, setPhaseStaack] = useState<Phase>(
    (localStorage.getItem("geo_phase_staack") as Phase) || "new"
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState<GenerateResult | null>(null);
  const [history, setHistory] = useState<GeoAnswerRow[]>([]);
  const [expanded, setExpanded] = useState<number | null>(null);
  const [prospects, setProspects] = useState<ProspectRow[]>([]);
  const [loadedProspect, setLoadedProspect] = useState<ProspectRow | null>(null);
  const [showAllProspects, setShowAllProspects] = useState(false);
  const [scanRunning, setScanRunning] = useState(false);
  const [scanMsg, setScanMsg] = useState("");
  const [priorityKw, setPriorityKw] = useState("");
  const [savingKw, setSavingKw] = useState(false);

  useEffect(() => {
    localStorage.setItem("geo_phase_bryce", phaseBryce);
  }, [phaseBryce]);
  useEffect(() => {
    localStorage.setItem("geo_phase_growl", phaseGrowl);
  }, [phaseGrowl]);
  useEffect(() => {
    localStorage.setItem("geo_phase_rajveer", phaseRajveer);
  }, [phaseRajveer]);
  useEffect(() => {
    localStorage.setItem("geo_phase_staack", phaseStaack);
  }, [phaseStaack]);

  const loadHistory = async () => {
    try {
      const res = await fetch(`${API_BASE}/list?limit=30`, { headers: authHeaders() });
      const data = await res.json();
      if (data.status === "success") setHistory(data.rows);
    } catch {
      /* ignore */
    }
  };

  const loadProspects = async (all = showAllProspects) => {
    try {
      const res = await fetch(`${API_BASE}/prospects?limit=50${all ? "&all=1" : ""}`, {
        headers: authHeaders(),
      });
      const data = await res.json();
      if (data.status === "success") setProspects(data.rows);
    } catch {
      /* ignore */
    }
  };

  const pollScan = (attempt = 0) => {
    if (attempt > 60) {
      setScanRunning(false);
      setScanMsg("Scan is taking unusually long - refresh the list in a bit.");
      return;
    }
    setTimeout(async () => {
      try {
        const res = await fetch(`${API_BASE}/discover/status`, { headers: authHeaders() });
        const data = await res.json();
        const scan = data.scan || {};
        if (scan.running) {
          setScanMsg(`Scanning ${scan.lastPlatform}... (started ${new Date(scan.startedAt).toLocaleTimeString()})`);
          pollScan(attempt + 1);
        } else {
          setScanRunning(false);
          const base = scan.lastError ? `Scan error: ${scan.lastError}` : `Scan done - ${scan.lastFound} prospects found/updated.`;
          setScanMsg(scan.note ? `${base} ${scan.note}` : base);
          loadProspects();
        }
      } catch {
        pollScan(attempt + 1);
      }
    }, 10000);
  };

  const startScan = async (platform: string) => {
    setScanMsg("");
    setScanRunning(true);
    try {
      const res = await fetch(`${API_BASE}/discover`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({ platform }),
      });
      const data = await res.json();
      if (!res.ok) {
        setScanRunning(false);
        setScanMsg(data.message || "Could not start scan");
        return;
      }
      setScanMsg("Scan started - takes 2-5 minutes. The list refreshes automatically.");
      pollScan();
    } catch (e: any) {
      setScanRunning(false);
      setScanMsg(e.message || "Could not start scan");
    }
  };

  const setProspectStatus = async (row: ProspectRow, status: string) => {
    try {
      await fetch(`${API_BASE}/prospects/${row.id}/status`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({ status }),
      });
      loadProspects();
    } catch {
      /* ignore */
    }
  };

  // Load a suggestion into the generation box. Does NOT mark it answered yet — it stays in the
  // list (still skippable) and is only marked "used" once you actually generate an answer for it.
  const applyProspect = (row: ProspectRow) => {
    setUrl(row.url);
    setContent(row.title + (row.snippet ? `\n\n${row.snippet}` : ""));
    setKeyword(row.keyword || "");
    setLoadedProspect(row);
    // auto-select an account that hasn't answered this question yet, so rotation is correct
    if (row.remaining_accounts && row.remaining_accounts.length > 0) {
      setAccount(row.remaining_accounts[0]);
    }
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const loadPriorityKw = async () => {
    try {
      const r = await fetch(`${API_BASE}/settings`, { headers: authHeaders() });
      const d = await r.json();
      if (d.status === "success") setPriorityKw(d.priority_keywords || "");
    } catch { /* ignore */ }
  };
  const savePriorityKw = async () => {
    setSavingKw(true);
    try {
      await fetch(`${API_BASE}/settings`, {
        method: "POST", headers: authHeaders(),
        body: JSON.stringify({ priority_keywords: priorityKw }),
      });
    } catch { /* ignore */ }
    setSavingKw(false);
  };

  useEffect(() => {
    loadHistory();
    loadProspects();
    loadPriorityKw();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const platformOfUrl = (u: string) =>
    /reddit\.com|redd\.it/i.test(u) ? "reddit" : /quora\.com/i.test(u) ? "quora" : "";

  const generate = async () => {
    if (!content.trim()) {
      setError("Paste the question/comment you found first.");
      return;
    }
    setError("");
    setResult(null);
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/generate`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({
          url,
          content,
          keyword,
          account,
          phases: { bryce: phaseBryce, growl: phaseGrowl, rajveer: phaseRajveer, staack: phaseStaack },
          prospect_id: loadedProspect ? loadedProspect.id : undefined,
        }),
      });
      const data = await res.json();
      if (!res.ok || data.status !== "success") {
        throw new Error(data.message || `Request failed (${res.status})`);
      }
      setResult(data);
      loadHistory();
      // the backend recorded which account answered this suggestion (starts the 4-day cooldown;
      // it re-appears for the remaining accounts once the cooldown passes). Just refresh the list.
      if (loadedProspect) {
        setLoadedProspect(null);
        loadProspects();
      }
    } catch (e: any) {
      setError(e.message || "Generation failed");
    } finally {
      setLoading(false);
    }
  };

  const togglePosted = async (row: GeoAnswerRow) => {
    try {
      await fetch(`${API_BASE}/${row.id}/posted`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({ posted: !row.posted }),
      });
      loadHistory();
    } catch {
      /* ignore */
    }
  };

  const detected = platformOfUrl(url);

  return (
    <div className="ml-0 lg:ml-[256px] p-4">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center gap-2 mb-1">
          <MessageSquare size={22} className="text-blue-600" />
          <h1 className="text-xl font-bold text-gray-100">GEO Answer Generator</h1>
        </div>
        <p className="text-sm text-gray-400 mb-4">
          Paste a Reddit/Quora URL + the question or comment. Get a paste-ready, compliant answer
          that follows the Growl Games GEO playbook (phases, region gate, disclosure).
        </p>

        {/* ---- form ---- */}
        <div className="bg-white rounded-lg shadow p-4 space-y-3">
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">
              Thread / Question URL {detected && (
                <span className={`ml-2 px-2 py-0.5 rounded-full text-[10px] uppercase text-white ${detected === "reddit" ? "bg-orange-500" : "bg-red-700"}`}>
                  {detected}
                </span>
              )}
            </label>
            <input
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://www.reddit.com/r/CryptoGambling/comments/... or https://www.quora.com/..."
              className="w-full border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">
              Question / comment you're responding to *
            </label>
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              rows={5}
              placeholder="Paste the full question, post or comment text here..."
              className="w-full border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">
                Target keyword (optional)
              </label>
              <input
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="e.g. provably fair"
                className="w-full border rounded-md px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Answer as</label>
              <select
                value={account}
                onChange={(e) => setAccount(e.target.value)}
                className="w-full border rounded-md px-3 py-2 text-sm bg-white"
              >
                <option value="auto">Auto (let AI pick)</option>
                <option value="bryce">Bryce Yang (marketing mgr)</option>
                <option value="growl">Growl Games (brand)</option>
                <option value="rajveer">Rajveer Pawar (veteran player)</option>
                <option value="staack">Staack Games (studio)</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">
                Bryce account phase
              </label>
              <select
                value={phaseBryce}
                onChange={(e) => setPhaseBryce(e.target.value as Phase)}
                className="w-full border rounded-md px-3 py-2 text-sm bg-white"
              >
                {PHASES.map((p) => (
                  <option key={p} value={p}>{p}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">
                Growl account phase
              </label>
              <select
                value={phaseGrowl}
                onChange={(e) => setPhaseGrowl(e.target.value as Phase)}
                className="w-full border rounded-md px-3 py-2 text-sm bg-white"
              >
                {PHASES.map((p) => (
                  <option key={p} value={p}>{p}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">
                Rajveer account phase
              </label>
              <select
                value={phaseRajveer}
                onChange={(e) => setPhaseRajveer(e.target.value as Phase)}
                className="w-full border rounded-md px-3 py-2 text-sm bg-white"
              >
                {PHASES.map((p) => (
                  <option key={p} value={p}>{p}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">
                Staack account phase
              </label>
              <select
                value={phaseStaack}
                onChange={(e) => setPhaseStaack(e.target.value as Phase)}
                className="w-full border rounded-md px-3 py-2 text-sm bg-white"
              >
                {PHASES.map((p) => (
                  <option key={p} value={p}>{p}</option>
                ))}
              </select>
            </div>
          </div>

          {loadedProspect && (
            <div className="flex items-center gap-2 bg-indigo-50 border border-indigo-200 rounded-md px-3 py-2 text-xs text-indigo-800">
              <span className="flex-1 truncate">
                Loaded: <b>{loadedProspect.title || loadedProspect.url}</b>
                {loadedProspect.remaining_accounts && loadedProspect.answered_list && loadedProspect.answered_list.length > 0
                  ? ` — already answered by ${loadedProspect.answered_list.map((a) => ACCOUNT_LABEL[a] || a).join(", ")}; still to do: ${(loadedProspect.remaining_accounts || []).map((a) => ACCOUNT_LABEL[a] || a).join(", ")}. Generating with the selected account records it (4-day cooldown before the next account).`
                  : " — generating records the selected account (then a 4-day cooldown before another account can answer it)."}
              </span>
              <button
                onClick={() => { const p = loadedProspect; setLoadedProspect(null); setUrl(""); setContent(""); setKeyword(""); setProspectStatus(p, "skipped"); }}
                className="bg-white hover:bg-gray-100 border border-indigo-200 text-indigo-700 px-3 py-1 rounded text-xs font-semibold whitespace-nowrap"
              >
                Skip this question
              </button>
              <button
                onClick={() => { setLoadedProspect(null); setUrl(""); setContent(""); setKeyword(""); }}
                className="text-indigo-400 hover:text-indigo-700 px-1"
                title="Clear the form without skipping"
              >
                ✕
              </button>
            </div>
          )}
          <div className="flex items-center gap-3">
            <button
              onClick={generate}
              disabled={loading}
              className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white px-5 py-2 rounded-md text-sm font-semibold"
            >
              {loading ? <Loader size={15} className="animate-spin" /> : <Sparkles size={15} />}
              {loading ? "Generating (can take up to a minute)..." : "Generate Answer"}
            </button>
            {error && <span className="text-sm text-red-600">{error}</span>}
          </div>
          <p className="text-[11px] text-gray-400">
            Phase reminder: <b>new</b> = zero brand mentions · <b>warming</b> = mention ~1 in 5
            relevant threads · <b>established</b> = mention on relevant threads (≤1 in 4-5 overall).
            Leave "Target keyword" blank to let the AI pick the best-fitting priority keyword.
          </p>
        </div>

        {/* ---- priority keywords ---- */}
        <details className="bg-white rounded-lg shadow mt-4">
          <summary className="cursor-pointer px-4 py-3 text-sm font-semibold text-gray-700 flex items-center gap-2">
            <Search size={15} className="text-blue-600" /> Priority target keywords
            <span className="font-normal text-gray-400">— the AI picks the best-fitting one for each answer (unless you type a keyword above)</span>
          </summary>
          <div className="px-4 pb-4">
            <p className="text-[11px] text-gray-500 mb-2">
              One keyword per line. For each answer the AI uses the most relevant keyword from this
              list; it only falls back to an off-list keyword when none of these genuinely fit the
              thread. Region-specific keywords (uk/canada/australia/india/usa/europe) are matched to
              the thread's region when possible. A manually-typed "Target keyword" always overrides.
            </p>
            <textarea
              value={priorityKw}
              onChange={(e) => setPriorityKw(e.target.value)}
              rows={10}
              placeholder="one target keyword per line..."
              className="w-full border rounded px-3 py-2 text-xs font-mono"
            />
            <button
              onClick={savePriorityKw}
              disabled={savingKw}
              className="mt-2 flex items-center gap-1 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white px-4 py-1.5 rounded-md text-xs font-semibold"
            >
              {savingKw ? <Loader size={13} className="animate-spin" /> : <Check size={13} />}
              Save priority keywords
            </button>
          </div>
        </details>

        {/* ---- result ---- */}
        {result && (
          <div className="mt-5 space-y-4">
            {result.meta && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <div className="text-xs font-bold text-yellow-800 uppercase mb-2">
                  Analysis / compliance check
                </div>
                <pre className="text-xs text-gray-700 whitespace-pre-wrap font-sans">{result.meta}</pre>
              </div>
            )}
            <div className="bg-white border-2 border-blue-500 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="text-xs font-bold text-blue-700 uppercase">
                  Paste-ready answer ({result.platform})
                  <span className="ml-2 font-normal normal-case text-gray-400">via {result.model}</span>
                </div>
                <CopyButton text={result.answer} label="Copy answer" />
              </div>
              <pre className="text-sm text-gray-800 whitespace-pre-wrap font-sans">{result.answer}</pre>
            </div>
            {result.alt_answer && (
              <div className="bg-white border rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-xs font-bold text-gray-500 uppercase">
                    Alt version (no brand mention)
                  </div>
                  <CopyButton text={result.alt_answer} label="Copy alt" />
                </div>
                <pre className="text-sm text-gray-700 whitespace-pre-wrap font-sans">{result.alt_answer}</pre>
              </div>
            )}
          </div>
        )}

        {/* ---- prospect finder ---- */}
        <div className="mt-8">
          <div className="flex flex-wrap items-center gap-3 mb-2">
            <h2 className="text-sm font-bold text-gray-200 uppercase flex-1">
              Prospect Finder <span className="font-normal normal-case text-gray-400">— up to 8 threads worth answering (AI searches only when you click Search)</span>
            </h2>
            <button
              onClick={() => startScan("both")}
              disabled={scanRunning}
              className="flex items-center gap-2 bg-purple-600 hover:bg-purple-700 disabled:opacity-60 text-white px-4 py-1.5 rounded-md text-xs font-semibold"
            >
              {scanRunning ? <Loader size={13} className="animate-spin" /> : <Search size={13} />}
              {scanRunning ? "Searching..." : "Search (Reddit + Quora)"}
            </button>
            <button
              onClick={() => startScan("reddit")}
              disabled={scanRunning}
              className="flex items-center gap-1 bg-orange-500 hover:bg-orange-600 disabled:opacity-60 text-white px-3 py-1.5 rounded-md text-xs font-semibold"
            >
              <Search size={12} /> Reddit
            </button>
            <button
              onClick={() => startScan("quora")}
              disabled={scanRunning}
              className="flex items-center gap-1 bg-red-700 hover:bg-red-800 disabled:opacity-60 text-white px-3 py-1.5 rounded-md text-xs font-semibold"
            >
              <Search size={12} /> Quora
            </button>
            <label className="flex items-center gap-1 text-xs text-gray-500 cursor-pointer">
              <input
                type="checkbox"
                checked={showAllProspects}
                onChange={(e) => { setShowAllProspects(e.target.checked); loadProspects(e.target.checked); }}
              />
              show used/skipped
            </label>
          </div>
          {scanMsg && <div className="text-xs text-gray-500 mb-2">{scanMsg}</div>}

          <div className="bg-white rounded-lg shadow divide-y">
            {prospects.length === 0 && (
              <div className="p-4 text-sm text-gray-400">
                No prospects yet - hit "Scan Reddit + Quora" to find threads worth answering.
              </div>
            )}
            {prospects.map((p) => (
              <div key={p.id} className="p-3 flex items-start gap-3">
                <span
                  className={`mt-0.5 w-7 h-7 flex items-center justify-center rounded-full text-xs font-bold text-white shrink-0 ${
                    p.score >= 8 ? "bg-green-600" : p.score >= 5 ? "bg-yellow-500" : "bg-gray-400"
                  }`}
                  title="GEO value score"
                >
                  {p.score}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className={`px-2 py-0.5 rounded-full text-[10px] uppercase text-white ${p.platform === "reddit" ? "bg-orange-500" : "bg-red-700"}`}>
                      {p.platform}
                    </span>
                    {!p.brand_eligible && (
                      <span className="px-2 py-0.5 rounded-full text-[10px] bg-gray-200 text-gray-600" title={p.region_note}>
                        no-mention region
                      </span>
                    )}
                    {p.region_note && p.brand_eligible && (
                      <span className="px-2 py-0.5 rounded-full text-[10px] bg-amber-100 text-amber-700" title={p.region_note}>
                        region note
                      </span>
                    )}
                    {p.status !== "new" && (
                      <span className="px-2 py-0.5 rounded-full text-[10px] bg-blue-100 text-blue-700 uppercase">{p.status}</span>
                    )}
                    <a
                      href={p.url}
                      target="_blank"
                      rel="noreferrer"
                      className="text-sm font-medium text-gray-800 hover:text-blue-700 truncate"
                    >
                      {p.title || p.url}
                    </a>
                  </div>
                  <div className="text-xs text-gray-500 mt-0.5">{p.reason}</div>
                  {p.keyword && (
                    <div className="text-[11px] text-gray-400 mt-0.5">keyword: {p.keyword}</div>
                  )}
                  {(p.answered_list && p.answered_list.length > 0) && (
                    <div className="text-[11px] mt-1 flex flex-wrap items-center gap-1">
                      <span className="text-gray-400">answered by:</span>
                      {p.answered_list.map((a) => (
                        <span key={a} className="px-1.5 py-0.5 rounded bg-green-100 text-green-700">{ACCOUNT_LABEL[a] || a}</span>
                      ))}
                      {p.remaining_accounts && p.remaining_accounts.length > 0 && (
                        <>
                          <span className="text-gray-400 ml-1">· still to do:</span>
                          {p.remaining_accounts.map((a) => (
                            <span key={a} className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">{ACCOUNT_LABEL[a] || a}</span>
                          ))}
                        </>
                      )}
                    </div>
                  )}
                </div>
                <div className="flex gap-2 shrink-0">
                  <button
                    onClick={() => applyProspect(p)}
                    className={`px-3 py-1 rounded-md text-xs font-semibold ${loadedProspect?.id === p.id ? "bg-blue-800 text-white ring-2 ring-blue-300" : "bg-blue-600 hover:bg-blue-700 text-white"}`}
                  >
                    {loadedProspect?.id === p.id ? "Loaded ↑" : "Use ↑"}
                  </button>
                  {p.status !== "skipped" && (
                    <button
                      onClick={() => { if (loadedProspect?.id === p.id) setLoadedProspect(null); setProspectStatus(p, "skipped"); }}
                      className="bg-gray-100 hover:bg-gray-200 text-gray-600 px-3 py-1 rounded-md text-xs"
                    >
                      Skip
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
          <p className="text-[11px] text-gray-400 mt-1">
            Returns up to 8 suggestions per search, and only runs when you click a Search button.
            Quora is searched via Claude Sonnet 4.6 (thinking off) on your Max plan. Reddit is scanned via
            the official Reddit API — it needs free script-app credentials (REDDIT_* keys in
            backend/.env; reddit.com/prefs/apps) because Reddit blocks all search engines except
            Google. Recovery/addiction and competitor subs are excluded automatically; verify each
            sub's rules before posting.
          </p>
        </div>

        {/* ---- history ---- */}
        <div className="mt-8">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-sm font-bold text-gray-200 uppercase">Recent answers</h2>
            <button
              onClick={loadHistory}
              className="flex items-center gap-1 text-xs text-blue-600 hover:underline"
            >
              <RefreshCw size={12} /> Refresh
            </button>
          </div>
          <div className="bg-white rounded-lg shadow divide-y">
            {history.length === 0 && (
              <div className="p-4 text-sm text-gray-400">Nothing generated yet.</div>
            )}
            {history.map((row) => (
              <div key={row.id} className="p-3">
                <div
                  className="flex items-center gap-2 cursor-pointer"
                  onClick={() => setExpanded(expanded === row.id ? null : row.id)}
                >
                  <span className={`px-2 py-0.5 rounded-full text-[10px] uppercase text-white ${row.platform === "reddit" ? "bg-orange-500" : "bg-red-700"}`}>
                    {row.platform}
                  </span>
                  <span className="text-sm text-gray-800 flex-1 truncate">{row.question}</span>
                  <button
                    onClick={(e) => { e.stopPropagation(); togglePosted(row); }}
                    className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                      row.posted ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"
                    }`}
                  >
                    {row.posted ? "POSTED" : "mark posted"}
                  </button>
                  <span className="text-[11px] text-gray-400 whitespace-nowrap">
                    {new Date(row.created_at).toLocaleString()}
                  </span>
                </div>
                {expanded === row.id && (
                  <div className="mt-3 space-y-3">
                    {row.url && (
                      <a
                        href={row.url}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1 text-xs text-blue-600 hover:underline"
                      >
                        <ExternalLink size={12} /> {row.url}
                      </a>
                    )}
                    <div className="bg-gray-50 rounded p-3">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-[10px] font-bold text-gray-500 uppercase">Answer</span>
                        <CopyButton text={row.answer} />
                      </div>
                      <pre className="text-xs text-gray-700 whitespace-pre-wrap font-sans">{row.answer}</pre>
                    </div>
                    {row.alt_answer && (
                      <div className="bg-gray-50 rounded p-3">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-[10px] font-bold text-gray-500 uppercase">Alt (no mention)</span>
                          <CopyButton text={row.alt_answer} />
                        </div>
                        <pre className="text-xs text-gray-700 whitespace-pre-wrap font-sans">{row.alt_answer}</pre>
                      </div>
                    )}
                    {row.meta && (
                      <pre className="text-[11px] text-gray-500 whitespace-pre-wrap font-sans bg-yellow-50 rounded p-3">{row.meta}</pre>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default GeoAnswers;
